import React, { useState, useEffect } from "react";

import { t } from "i18next";
import Select from "react-select";

import InputGroup from "../Inputs/InputGroup";
import BaseOverlayModal from "./BaseOverlayModal";
import LookupsAPI from "../../API/lookups.api";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";
import BaseActionButton from "../Buttons/BaseActionButton";
import { ReactComponent as CouponSVG } from "../../assets/svgs/coupon-1.svg";
import { SuccessAlert } from "../../utils/Alerts";
import CouponsAPI from "../../API/coupons.api";
import { validate } from "../../utils/ValidationHelpers";
import CouponSchemas from "../../schemas/coupon.schema";
import ValidationError from "../Shared/ValidationError";
import ProductTags from "../Tags/ProductTags";
import SwitchButton from "../Switches/SwitchButton";
import { isEmpty } from "lodash";
import PriceOptionsCard from "../Cards/PriceOptionsCard";
import { getDatePlusMonths } from "../../utils/DateHelper";

const MAX_PAGE_NUMBER = 2;

const CreateCouponOverlayModal = ({
    isVisible,
    setIsVisible,
    submitCallback,
}) => {
    //Data States
    const [error, setError] = useState({});
    const [errorStep2, setErrorStep2] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isFormValidStep2, setIsFormValidStep2] = useState(false);
    const [isFormSubmittedStep2, setIsFormSubmittedStep2] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [acceptedProductsLookup, setAcceptedProductsLookup] = useState([]);
    const [couponIdentity, setCouponIdentity] = useState({});
    const [couponPayload, setCouponPayload] = useState({
        isForever: false,
    });

    //General States
    const [currentPage, setCurrentPage] = useState(1);
    const [isProductsLookupLoading, setIsProductsLookupLoading] =
        useState(false);

    useEffect(() => {
        if (!isVisible) resetForms();
    }, [isVisible]);

    useEffect(() => {
        validate(
            couponIdentity,
            CouponSchemas.AddCouponSchemaStep1,
            setIsFormValid,
            setError,
            {
                context: {
                    isParametersIncluded: couponIdentity.couponType === "1",
                },
            }
        );
    }, [couponIdentity]);

    useEffect(() => {
        validate(
            couponPayload,
            CouponSchemas.AddCouponSchemaStep2,
            setIsFormValidStep2,
            setErrorStep2,
            {
                context: {
                    isForever: couponPayload?.isForever,
                },
            }
        );
    }, [couponPayload]);

    useEffect(() => {
        getAcceptedProducts();
    }, []);

    const getAcceptedProducts = async () => {
        setIsProductsLookupLoading(true);
        const result = await LookupsAPI.getCouponAcceptedProducts();
        setAcceptedProductsLookup(result);
        setIsProductsLookupLoading(false);
    };

    const discountTypeOptions = [
        {
            name: "percentage",
            value: "2",
            label: t("percentageDiscount"),
        },
        {
            name: "percentageAndAdditionalParameters",
            value: "1",
            label: t("PercentageAndParametersPromotion"),
        },
    ];

    const discountDurationOptions = [
        {
            name: "forever",
            value: "0",
            label: t("forever"),
        },
        {
            name: "oneUse",
            value: "1",
            label: t("oneTimeSubscription"),
        },
    ];

    const resetForms = () => {
        setError({});
        setErrorStep2({});
        setIsFormValid(false);
        setIsFormSubmitted(false);
        setIsFormValidStep2(false);
        setIsFormSubmittedStep2(false);
        setIsSubmitting(false);
        setCouponIdentity({});
        setCouponPayload({
            isForever: false,
        });
        setCurrentPage(1);
        setIsProductsLookupLoading(false);
    };
    const renderFields = (fields) => {
        return (
            fields &&
            fields.map((field, index) => {
                if (!field.hide) {
                    return (
                        <div className="input-group" key={index}>
                            <label
                                className={`${
                                    field.required ? "required" : ""
                                } uppercase`}
                            >
                                {field.label}
                            </label>
                            {field.input}
                        </div>
                    );
                }
            })
        );
    };

    const handleStep1OnChange = (e) => {
        setCouponIdentity((prevData) => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));
    };

    const handleStep2OnChange = (e) => {
        setCouponPayload((prevData) => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));
    };

    const handleToggleChange = () => {
        setCouponPayload((prevData) => ({
            ...prevData,
            isForever: !couponPayload?.isForever,
        }));
    };

    const handleStep1SelectOnChange = (value, action) => {
        setCouponIdentity((prevData) => ({
            ...prevData,
            [action.name]: value,
        }));
    };

    const resetParameters = () => {
        setCouponIdentity((prevData) => {
            return {
                ...prevData,
                parameters: [],
            };
        });
    };

    const getLeftSideFormStep1 = () => {
        const value =
            discountTypeOptions.find(
                (opt) => opt.value === couponIdentity.couponType
            )?.name || "";
        return (
            <div className="input-group">
                <label className="blue-label required">Coupon type</label>
                <VerticalRadioButtons
                    name="couponType"
                    options={discountTypeOptions}
                    onChangeHandler={(e) => {
                        if (e.target.value === "2") resetParameters();
                        handleStep1OnChange(e);
                    }}
                    checkedReference={value}
                    wrapperClassName="bg-white w-full h-2/3 rounded text-primary-color-blue my-1"
                />
                {isFormSubmitted && (
                    <ValidationError errorMessage={error["couponType"]} />
                )}
            </div>
        );
    };

    const getRightSideFormStep1 = () => {
        const fields = [
            {
                label: t("name"),
                input: (
                    <>
                        <input
                            type="text"
                            className="input-element"
                            name="name"
                            value={couponIdentity.name || ""}
                            onChange={handleStep1OnChange}
                            placeholder={t("name")}
                        />
                        {isFormSubmitted && (
                            <ValidationError errorMessage={error["name"]} />
                        )}
                    </>
                ),
                required: true,
            },
            {
                label: t("percentage"),
                input: (
                    <>
                        <InputGroup
                            type="number"
                            name="percentage"
                            icon="percentage"
                            customClassNames="mb-0"
                            onChangeHandler={handleStep1OnChange}
                            placeholder={t("percentagePlaceholder", {
                                value: 15,
                            })}
                            value={couponIdentity.percentage || ""}
                        />
                        {isFormSubmitted && (
                            <ValidationError
                                errorMessage={error["percentage"]}
                            />
                        )}
                    </>
                ),
                required: true,
            },
            {
                label: t("product"),
                input: (
                    <>
                        <Select
                            name="product"
                            options={acceptedProductsLookup}
                            isDisabled={isProductsLookupLoading}
                            isLoading={isProductsLookupLoading}
                            value={couponIdentity.product || []}
                            placeholder={t("selectPlaceholder", {
                                entity: t("product"),
                            })}
                            onChange={(value, action) => {
                                resetParameters();
                                handleStep1SelectOnChange(value, action);
                            }}
                        />
                        {isFormSubmitted && (
                            <ValidationError errorMessage={error["product"]} />
                        )}
                    </>
                ),
                required: true,
            },
            {
                hide: couponIdentity.couponType !== "1",
                label: t("parameters"),
                required: true,
                input: (
                    <>
                        <Select
                            name="parameters"
                            isMulti
                            options={
                                couponIdentity.product?.modelParameterVersions
                            }
                            isDisabled={
                                !couponIdentity.product
                                    ?.modelParameterVersions ||
                                isProductsLookupLoading
                            }
                            placeholder={t("selectPlaceholder", {
                                entity: t("parameters"),
                            })}
                            isLoading={isProductsLookupLoading}
                            value={couponIdentity.parameters || null}
                            onChange={handleStep1SelectOnChange}
                        />
                        {isFormSubmitted && (
                            <ValidationError
                                errorMessage={error["parameters"]}
                            />
                        )}
                    </>
                ),
            },
        ];

        return renderFields(fields);
    };

    const getLeftSideFormStep2 = () => {
        const getPrices = () => {
            return couponIdentity.product?.subscriptionPlans.map((p) => ({
                name: p.durationType,
                price: p.price,
                currencyName: p.currencyName,
            }));
        };

        return (
            <div>
                <div className="input-group">
                    <label className="uppercase">{t("prices")}</label>
                    <PriceOptionsCard
                        pricePlans={getPrices()}
                        isPreview={true}
                        percentageDiscount={couponIdentity.percentage}
                        buttonClassname="bg-white"
                        isBorderLess={true}
                        isVertical={true}
                    />
                </div>
                {!isEmpty(couponIdentity?.parameters) && (
                    <div className="input-group pt-2">
                        <label className="uppercase">
                            {t("addedParameter")}
                        </label>

                        <div className="flex items-center">
                            <div
                                style={{ minWidth: "1rem" }}
                                className="w-4 h-4 rounded-full border border-primary-color-orange relative flex justify-center items-center mr-2"
                            >
                                <i className="fas fa-plus fa-xs absolute text-primary-color-orange"></i>
                            </div>
                            <ProductTags
                                tags={couponIdentity?.parameters}
                                listClassName="green-tags"
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const getRightSideFormStep2 = () => {
        const { start, end } = getDatePlusMonths(6);

        const fields = [
            {
                label: "",
                input: (
                    <>
                        <div className="flex items-center gap-4">
                            <div className="flex flex-col w-4/5">
                                <label className="uppercase text-xs required">
                                    {t("expirationDate")}
                                </label>
                                <input
                                    type="date"
                                    name="expiresAt"
                                    onChange={handleStep2OnChange}
                                    min={start}
                                    max={end}
                                    className={`input-element ${
                                        couponPayload?.isForever
                                            ? "disabled"
                                            : ""
                                    }`}
                                />
                            </div>
                            <SwitchButton
                                label={t("forever")}
                                checked={couponPayload?.isForever}
                                onChange={handleToggleChange}
                            />
                        </div>
                        {isFormSubmittedStep2 && (
                            <ValidationError
                                errorMessage={errorStep2["expiresAt"]}
                            />
                        )}
                    </>
                ),
            },
            {
                label: t("numberOfUse"),
                required: true,
                input: (
                    <>
                        <VerticalRadioButtons
                            name="durationType"
                            options={discountDurationOptions}
                            onChangeHandler={handleStep2OnChange}
                            checkedReference={
                                discountDurationOptions.find(
                                    (opt) =>
                                        opt.value === couponPayload.durationType
                                )?.name || ""
                            }
                        />
                    </>
                ),
            },
            {
                label: t("quantityTitle", { entity: t("promotionCodes") }),
                required: true,
                input: (
                    <>
                        <div className="flex items-center">
                            <input
                                type="number"
                                name="quantity"
                                value={couponPayload?.quantity ?? ""}
                                onChange={handleStep2OnChange}
                                className="input-element"
                                placeholder={t("decimalPlacesPlaceholder")}
                            />
                            <BaseActionButton
                                className="table-btn table-btn-blue mx-1"
                                icon="fas fa-plus"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setCouponPayload((prevData) => ({
                                        ...prevData,
                                        quantity: couponPayload?.quantity
                                            ? couponPayload?.quantity + 1
                                            : 1,
                                    }));
                                }}
                                disabled={couponPayload?.quantity >= 10}
                            />
                            <BaseActionButton
                                className="table-btn table-btn-blue"
                                icon="fas fa-minus"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setCouponPayload((prevData) => ({
                                        ...prevData,
                                        quantity: couponPayload?.quantity
                                            ? couponPayload?.quantity - 1
                                            : 0,
                                    }));
                                }}
                                disabled={couponPayload?.quantity <= 1}
                            />
                        </div>
                        {isFormSubmittedStep2 && (
                            <ValidationError
                                errorMessage={errorStep2["quantity"]}
                            />
                        )}
                    </>
                ),
            },
        ];

        return renderFields(fields);
    };

    const getLeftContent = () => {
        switch (currentPage) {
            case 1:
                return getLeftSideFormStep1();
            case 2:
                return getLeftSideFormStep2();
        }
    };

    const getRightContent = () => {
        switch (currentPage) {
            case 1:
                return getRightSideFormStep1();
            case 2:
                return getRightSideFormStep2();
        }
    };

    const incementPage = () => {
        setIsFormSubmitted(true);
        if (!isFormValid) return;
        setCurrentPage((prev) => prev + 1);
    };

    const decrementPage = () => {
        setCouponPayload({
            isForever: false,
        });
        setCurrentPage((prev) => prev - 1);
    };

    const submit = () => {
        setIsSubmitting(true);
        setIsFormSubmittedStep2(true);
        if (!isFormValidStep2) {
            setIsSubmitting(false);
            return;
        }
        const formattedPayload = {
            name: couponIdentity.name,
            type: +couponIdentity.couponType,
            percentageDiscount: +couponIdentity.percentage,
            duration: +couponPayload.durationType,
            productId: couponIdentity.product?.value,
            quantity: +couponPayload.quantity,
            ...(!couponPayload.isForever && {
                expiresAt: couponPayload.expiresAt,
            }),
            modelParameterProductVersionIds:
                couponIdentity.couponType === "1"
                    ? couponIdentity?.parameters?.map(
                          (parameter) => parameter.value
                      )
                    : [],
        };

        addCoupon(formattedPayload);
    };

    const addCoupon = async (payload) => {
        const result = await CouponsAPI.addCoupon(payload);
        setIsSubmitting(false);
        if (result?.inserted) {
            setIsVisible(false);
            submitCallback();
            resetForms();
            SuccessAlert(t("addSuccessMessage", { entity: t("coupon") }));
        }
    };

    return (
        <>
            <BaseOverlayModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                title="test"
            >
                <div className="wrapper-left pb-0">
                    <div className="flex flex-col w-full h-full justify-between">
                        <div className="flex flex-col justify-between items-center h-1/3">
                            <div className="flex justify-center">
                                <CouponSVG width="14rem" />
                            </div>
                            {getLeftContent()}
                        </div>
                        {currentPage > 1 && (
                            <button
                                onClick={decrementPage}
                                className={
                                    "secondary-green-btn mng-model modal-btn-open mr-auto mb-4 self-end"
                                }
                            >
                                <i className="fas fa-caret-left"></i>
                                <span className={"ml-2"}>{t("back")}</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="wrapper-right h-full">
                    <div className="main-content-header w-full">
                        <div className="flex justify-between">
                            <h3>{t("couponDetails")}</h3>
                        </div>
                        <p>{t("couponDetailsDescription")}</p>
                    </div>
                    <div className="flex flex-col w-full h-full justify-between">
                        <div className="flex flex-col">{getRightContent()}</div>
                        {currentPage < MAX_PAGE_NUMBER && (
                            <button
                                onClick={incementPage}
                                className={
                                    "secondary-green-btn mng-model modal-btn-open ml-auto mb-4 self-end"
                                }
                                disabled={!isFormValid && isFormSubmitted}
                            >
                                <span className={"mr-2"}>{t("next")}</span>
                                <i className="fas fa-caret-right"></i>
                            </button>
                        )}
                        {currentPage == MAX_PAGE_NUMBER && (
                            <button
                                onClick={submit}
                                className={
                                    "secondary-green-btn mng-model modal-btn-open ml-auto mb-4 self-end"
                                }
                                disabled={
                                    (!isFormValidStep2 &&
                                        isFormSubmittedStep2) ||
                                    isSubmitting
                                }
                            >
                                <span className={"mr-2"}>
                                    {t(isSubmitting ? "requesting" : "submit")}
                                </span>
                                <i className="fas fa-caret-right"></i>
                            </button>
                        )}
                    </div>
                </div>
            </BaseOverlayModal>
        </>
    );
};

export default CreateCouponOverlayModal;
